import React, { useState } from 'react';
import { X, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { RetrospectiveBoard, ExportOptions } from '../types';
import toast from 'react-hot-toast';
import { useApiClient } from '../utils/authenticatedApiClient';
import { API_ENDPOINTS } from '../utils/apiClient';

interface ExportModalProps {
  board: RetrospectiveBoard;
  onClose: () => void;
}

const ExportModal: React.FC<ExportModalProps> = ({ board, onClose }) => {
  const apiClient = useApiClient();
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    format: 'excel',
    includeActionItems: true,
    includeTimestamps: true,
    groupByColumn: true,
  });
  
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      const response = await apiClient.postForFile(API_ENDPOINTS.EXPORT, {
        boardId: board.id,
        options: exportOptions,
      });

      if (!response.blob) {
        throw new Error(response.error || 'Export failed');
      }

      // Handle file download
      const blob = response.blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Generate filename based on format and board title
      const sanitizedTitle = board.title.replace(/[^a-zA-Z0-9]/g, '_');
      const extension = exportOptions.format === 'excel' ? 'xlsx' : 
                      exportOptions.format === 'pdf' ? 'pdf' : 'json';
      a.download = `${sanitizedTitle}_${new Date().toISOString().split('T')[0]}.${extension}`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completed successfully!');
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export board');
    } finally {
      setIsExporting(false);
    }
  };

  const getFormatIcon = (format: ExportOptions['format']) => {
    switch (format) {
      case 'excel': return <FileSpreadsheet className="h-5 w-5" />;
      case 'pdf': return <FileText className="h-5 w-5" />;
      case 'json': return <FileText className="h-5 w-5" />;
    }
  };

  const formatDescriptions = {
    excel: 'Export as Excel spreadsheet (.xlsx) with formatted columns and data',
    pdf: 'Export as PDF document with visual representation of the board',
    json: 'Export as JSON file with complete board data for backup or integration'
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 text-center">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all max-w-md w-full">
          <div className="bg-white px-6 pt-6 pb-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Download className="h-5 w-5 text-indigo-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">
                  Export Board
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Format Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Export Format
                </label>
                <div className="space-y-2">
                  {(['excel', 'pdf', 'json'] as ExportOptions['format'][]).map((format) => (
                    <label
                      key={format}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${ 
                        exportOptions.format === format
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="format"
                        value={format}
                        checked={exportOptions.format === format}
                        onChange={(e) => setExportOptions({ 
                          ...exportOptions, 
                          format: e.target.value as ExportOptions['format']
                        })}
                        className="sr-only"
                      />
                      <div className="flex items-center flex-1">
                        <div className={`mr-3 ${exportOptions.format === format ? 'text-indigo-600' : 'text-gray-400'}`}>
                          {getFormatIcon(format)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 capitalize">
                            {format}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDescriptions[format]}
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Export Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Export Options
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeActionItems}
                      onChange={(e) => setExportOptions({
                        ...exportOptions,
                        includeActionItems: e.target.checked
                      })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      Include action items
                    </span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeTimestamps}
                      onChange={(e) => setExportOptions({
                        ...exportOptions,
                        includeTimestamps: e.target.checked
                      })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      Include timestamps
                    </span>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportOptions.groupByColumn}
                      onChange={(e) => setExportOptions({
                        ...exportOptions,
                        groupByColumn: e.target.checked
                      })}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      Group notes by column
                    </span>
                  </label>
                </div>
              </div>
              
              {/* Board Summary */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Export Summary</h4>
                <div className="text-xs text-gray-600 space-y-1">
                  <div>Board: {board.title}</div>
                  <div>Notes: {board.columns.reduce((sum, col) => sum + col.notes.length, 0)}</div>
                  <div>Action Items: {board.actionItems.length}</div>
                  <div>Columns: {board.columns.length}</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 transition-colors"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;